import React, { useCallback, useState } from 'react';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { RubricRoleOperation } from 'libs/end-to-end/src/types/rubrics/rubric';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { FormControl } from '@mui/material';
import { FormControlLabel } from '@mui/material';
import { FormLabel } from '@mui/material';
import { RadioGroup } from '@mui/material';
import { Radio } from '@mui/material';
import { TextField } from '@mui/material';
import { EditRubricConfirmEditButton } from 'apps/sigma2-ui/src/components/rubrics/EditRubricConfirmEditButton';
import { EditRubricCancelEditButton } from 'apps/sigma2-ui/src/components/rubrics/EditRubricCancelEditButton';
import { EditRubricRoleProps } from 'apps/sigma2-ui/src/components/rubrics/EditRubricRoleProps';

export const EditRubricEditRoleRule: React.FC<EditRubricRoleProps> = ({
  roleOperation,
  roleName,
  onRoleOperationChange,
  onRoleNameChange,
  onRoleSave,
  onRoleCancel,
  requiredRoleError,
  duplicateRoleError,
}) => {
  const language = useLanguage();
  const [touched, setTouched] = useState(false);

  const handleOperationChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const parsedOp = RubricRoleOperation.safeParse(e.currentTarget.value);
      if (!parsedOp.success) {
        return;
      }

      onRoleOperationChange(parsedOp.data);
    },
    [onRoleOperationChange],
  );

  return (
    <Spacing direction="vertical" size="gap-5">
      <FormControl>
        <FormLabel id="edit-rubric-edit-role-rule-label">
          <S2Typography
            variant="body-7"
            textColorVariant="gray-700"
            translatedContent={language.rubricEditRoleOperationJobTitleLabel()}
          />
        </FormLabel>

        <Spacing direction="horizontal" size="lg" className="items-center">
          <RadioGroup
            aria-labelledby="edit-rubric-edit-role-rule-label"
            value={roleOperation}
            onChange={handleOperationChange}
            className="gap-4"
            row
            sx={{
              '& .MuiRadio-root:first-of-type': {
                paddingLeft: 0,
              },
            }}
          >
            {RubricRoleOperation.options.map((op) => (
              <FormControlLabel
                key={op}
                value={op}
                label={language.rubricRoleOperationDisplay({ operation: op })}
                control={<Radio className="text-indigo-1000" />}
              />
            ))}
          </RadioGroup>

          <TextField
            variant="outlined"
            size="small"
            className="flex-1"
            value={roleName}
            error={touched && (requiredRoleError || duplicateRoleError)}
            helperText={
              touched && requiredRoleError
                ? language.rubricRoleNameRequired()
                : duplicateRoleError
                  ? language.rubricRoleNameAlreadyInUse()
                  : undefined
            }
            onChange={(e) => {
              if (!touched) setTouched(true);
              onRoleNameChange(e.currentTarget.value);
            }}
            placeholder={language.rubricEditRoleOperationJobTitlePlaceholder()}
          />
          <Spacing direction="horizontal" size="md" className="ml-auto">
            <EditRubricCancelEditButton onClick={onRoleCancel} />
            <EditRubricConfirmEditButton
              onClick={onRoleSave}
              disabled={requiredRoleError || duplicateRoleError}
            />
          </Spacing>
        </Spacing>
      </FormControl>
    </Spacing>
  );
};
